import { z } from 'zod'

const DetailDataUnknownObjectSchema = z.object({
  name: z.string(),
  data: z.record(z.string(), z.unknown()),
})

const DetailDataMetaSchema = DetailDataUnknownObjectSchema.extend({
  type: z.literal('meta'),
})

const DetailDataArraySchema = DetailDataUnknownObjectSchema.extend({
  type: z.literal('array'),
})

const DetailDataSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('text'),
    name: z.string(),
    data: z.string(),
  }),
  DetailDataArraySchema,
  DetailDataMetaSchema,
])

export const ItemSchema = z.object({
  type: z.string(),
  level: z.string(),
  description: z.string().nullable(),
  timestamp: z.number(),
  trace_id: z.string(),
  subscriber: z.object({ id: z.string(), name: z.string().nullable() }),
  details: z.array(DetailDataSchema),
  chns: z.string().nullable(),
  flow_ns: z.string().nullable(),
  flow_link: z.string().nullable(),
  content_id: z.string().or(z.number()).nullable(),
})

const PolicyEnforcementLogSchema = z.object({
  trace_id: z.string().optional(),
  ts: z.number(),
  level: z.string(),
  description: z.string().optional(),
  type: z.string(),
  reason: z.string(),
  notification_id: z.number().optional(),
})

export type DevLogItem = z.infer<typeof ItemSchema>
export type DevLogDetail = z.infer<typeof DetailDataSchema>
export type DetailDataArray = z.infer<typeof DetailDataArraySchema>
export type DevLogDetailMeta = z.infer<typeof DetailDataMetaSchema>
export type PolicyEnforcementLogItem = z.infer<typeof PolicyEnforcementLogSchema>

export const DevLogsApiSchemas = {
  markAsRead: {
    request: z.undefined(),
    query: z.undefined(),
    path: z.undefined(),
    response: z.object({
      accountUser: z.object({
        last_devlog_message_read_ts: z.number(),
      }),
    }),
  },
  fetchList: {
    query: z.object({ limiter: z.number().nullish() }).optional(),
    path: z.undefined(),
    response: z.object({
      list: z.array(ItemSchema),
      limiter: z.number().nullable(),
      total: z.object({
        success: z.number(),
        critical: z.number(),
        error: z.number(),
        warning: z.number(),
        info: z.number(),
      }),
    }),
  },
  fetchItem: {
    query: z.object({ token: z.string() }),
    path: z.undefined(),
    response: z.object({
      log: ItemSchema,
    }),
  },
  fetchPolicyEnforcementLogs: {
    query: z.undefined(),
    path: z.undefined(),
    response: z.object({
      logs: z.array(PolicyEnforcementLogSchema),
    }),
  },
}

export type DevLogTotalCounts = z.infer<typeof DevLogsApiSchemas.fetchList.response>['total']
